import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
    ArrowLeft, 
    Camera, 
    Sparkles, 
    Headphones, 
    Route, 
    Gamepad2, 
    Wallet, 
    ChevronRight,
    Map,
    Compass
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const HelpPage = () => {
    const navigate = useNavigate();

    // Dinamik Maliyetler İçin State
    const [costs, setCosts] = useState({
        analyze_quick: '...',
        generate_ai_comment: '...',
        audio_narration: '...',
        ai_chat_prompt: '...',
        create_trip: '...',
        quiz_reward_perfect: '...'
    });

    // Sayfa yüklendiğinde güncel maliyetleri Backend'den çek
    useEffect(() => {
        const fetchCosts = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/features/costs`);
                setCosts({
                    // DB'den gelen değerleri al, yoksa varsayılan değerleri (fallback) kullan
                    analyze_quick: response.data.analyze_quick || 100,
                    generate_ai_comment: response.data.generate_ai_comment || 20,
                    audio_narration: response.data.audio_narration || 10,
                    ai_chat_prompt: response.data.ai_chat_prompt || 50,
                    create_trip: response.data.create_trip || 100,
                    quiz_reward_perfect: response.data.quiz_reward_perfect || 100
                });
            } catch (error) {
                console.error("Maliyetler çekilemedi, varsayılan değerler gösteriliyor.", error);
                // API hatası durumunda uygulamanın çökmemesi için varsayılanlar
                setCosts({
                    analyze_quick: 100,
                    generate_ai_comment: 20,
                    audio_narration: 10,
                    ai_chat_prompt: 50,
                    create_trip: 100
                });
            }
        };
        fetchCosts();
    }, []);

    // Animasyon varyantları
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const FeatureCard = ({ icon: Icon, title, token, description, colorClass, borderClass }) => (
        <motion.div 
            variants={itemVariants}
            className={`relative bg-slate-800/50 backdrop-blur-xl p-6 md:p-8 rounded-3xl border transition-all duration-300 hover:-translate-y-2 group overflow-hidden ${borderClass}`}
        >
            {/* Arka Plan Parlaması */}
            <div className={`absolute -right-10 -top-10 w-32 h-32 rounded-full blur-3xl opacity-10 group-hover:opacity-20 transition-opacity ${colorClass.replace('text-', 'bg-')}`}></div>
            
            <div className="flex justify-between items-start mb-6 relative z-10">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${colorClass.replace('text-', 'bg-')}/20 ${colorClass}`}>
                    <Icon size={28} />
                </div>
                {token && (
                    <div className="flex items-center gap-1.5 bg-slate-900/80 px-3 py-1.5 rounded-lg border border-slate-700">
                        <Wallet size={14} className="text-blue-400" />
                        <span className="text-xs font-bold text-slate-300">
                            {token} {token.toString().includes('ÖDÜL') ? '' : 'Token'}
                        </span>
                    </div>
                )}
            </div>
            
            <h3 className="text-xl font-bold text-white mb-3 relative z-10 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-slate-400 transition-all">
                {title}
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed relative z-10">
                {description}
            </p>
        </motion.div>
    );

    return (
        <div className="min-h-screen bg-[#0f172a] text-slate-200 font-sans selection:bg-blue-500/30 overflow-x-hidden pb-24">
            
            {/* Dekoratif Arka Plan */}
            <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/10 rounded-full blur-[120px]" />
            </div>

            {/* Üst Navigasyon */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 pt-8 pb-4">
                <button 
                    onClick={() => navigate(-1)} 
                    className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors bg-slate-800/50 hover:bg-slate-800 px-4 py-2 rounded-full border border-slate-700/50 w-fit"
                >
                    <ArrowLeft size={18} /> Geri Dön
                </button>
            </div>

            <main className="relative z-10 max-w-7xl mx-auto px-6">
                
                {/* Hero / Başlık Kısmı */}
                <motion.div 
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center max-w-3xl mx-auto mt-8 mb-16"
                >
                    <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
                        <Compass size={16} /> Mekânname Rehberi
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight leading-tight">
                        Tarihi Keşfetmenin <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">En Akıllı Yolu</span>
                    </h1>
                    <p className="text-lg text-slate-400 leading-relaxed">
                        Mekânname, yapay zeka destekli bir gezi asistanıdır. Karşılaştığın tarihi yapıları tanır, sana özel rotalar çizer ve tarihsel hikayeleri seslendirir.
                    </p>
                </motion.div>

                {/* Özellikler Grid'i */}
                <motion.div 
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16"
                >
                    <FeatureCard 
                        icon={Camera}
                        title="Fotoğraf Analizi"
                        token={costs.analyze_quick}
                        description="Neresi olduğunu bilmediğin bir tarihi yapının fotoğrafını çek veya yükle. Yapay zeka anında mekanı tanısın ve tüm tarihçesini önüne sersin."
                        colorClass="text-blue-500"
                        borderClass="border-slate-700/50 hover:border-blue-500/50 shadow-[0_0_0_rgba(59,130,246,0)] hover:shadow-[0_10px_40px_rgba(59,130,246,0.1)]"
                    />

                    <FeatureCard 
                        icon={Sparkles}
                        title="Yapay Zeka Yorumu"
                        token={costs.generate_ai_comment}
                        description="Çektiğin fotoğrafın mimari açıdan nasıl durduğunu merak ediyor musun? AI modelimiz görseli inceleyip sana özel edebi bir sanat yorumu yapar."
                        colorClass="text-purple-500"
                        borderClass="border-slate-700/50 hover:border-purple-500/50 shadow-[0_0_0_rgba(168,85,247,0)] hover:shadow-[0_10px_40px_rgba(168,85,247,0.1)]"
                    />

                    <FeatureCard 
                        icon={Headphones}
                        title="Sesli Hikayeler"
                        token={costs.audio_narration}
                        description="Belgesel, romantik veya çocuklara özel... Tarzı sen seç, yapay zeka mekanın sırlarını senin için stüdyo kalitesinde seslendirsin."
                        colorClass="text-cyan-500"
                        borderClass="border-slate-700/50 hover:border-cyan-500/50 shadow-[0_0_0_rgba(6,182,212,0)] hover:shadow-[0_10px_40px_rgba(6,182,212,0.1)]"
                    />

                    <FeatureCard 
                        icon={Route}
                        title="Gezi Asistanı & Rotalar"
                        token={`${costs.ai_chat_prompt} / ${costs.create_trip}`}
                        description={`Nereye, kiminle ve ne kadar bütçeyle gideceğini söyle. Normal sohbetler ${costs.ai_chat_prompt}, detaylı gezi rotası çizimi ${costs.create_trip} tokendir.`}
                        colorClass="text-indigo-500"
                        borderClass="border-slate-700/50 hover:border-indigo-500/50 shadow-[0_0_0_rgba(99,102,241,0)] hover:shadow-[0_10px_40px_rgba(99,102,241,0.1)]"
                    />

                    <FeatureCard 
                        icon={Gamepad2}
                        title="Quiz Çöz & Token Kazan"
                        token={`ÖDÜL: +${costs.quiz_reward_perfect}`}
                        description="Gezdiğin rotalardaki veya ilgini çeken konulardaki tarih quizlerini %100 doğru tamamla, anında cüzdanına ücretsiz 100 Token eklensin!"
                        colorClass="text-yellow-500"
                        borderClass="border-slate-700/50 hover:border-yellow-500/50 shadow-[0_0_0_rgba(234,179,8,0)] hover:shadow-[0_10px_40px_rgba(234,179,8,0.1)]"
                    />

                    <FeatureCard 
                        icon={Wallet}
                        title="Token Sistemi (Kredi)"
                        token={null}
                        description="Sistemdeki özel AI işlemlerini Token ile yaparsın. Kayıt olduğunda 300 Token hoş geldin hediyesi alırsın. Bittiğinde mağazadan yenileyebilirsin."
                        colorClass="text-emerald-500"
                        borderClass="border-slate-700/50 hover:border-emerald-500/50 shadow-[0_0_0_rgba(16,185,129,0)] hover:shadow-[0_10px_40px_rgba(16,185,129,0.1)]"
                    />
                </motion.div>

                {/* Alt CTA (Call to Action) */}
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[2.5rem] p-8 md:p-12 text-center relative overflow-hidden shadow-2xl shadow-blue-900/30"
                >
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/20 rounded-full blur-3xl mix-blend-overlay"></div>
                    
                    <h2 className="text-3xl md:text-4xl font-black text-white mb-4 relative z-10">Maceraya Hazır mısın?</h2>
                    <p className="text-blue-100 mb-8 max-w-2xl mx-auto relative z-10 text-lg">
                        Gezmek istediğin ilk yeri bul veya hemen galerinden bir fotoğraf yükleyerek tarihi keşfetmeye başla.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
                        <button 
                            onClick={() => navigate('/upload')}
                            className="px-8 py-4 bg-white text-blue-600 font-bold rounded-xl shadow-xl hover:bg-slate-50 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                        >
                            <Camera size={20} /> Fotoğraf Analiz Et
                        </button>
                        <button 
                            onClick={() => navigate('/assistant')}
                            className="px-8 py-4 bg-indigo-800/50 hover:bg-indigo-800 text-white border border-indigo-400/30 font-bold rounded-xl shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 backdrop-blur-sm"
                        >
                            <Map size={20} /> Rota Oluştur
                        </button>
                    </div>
                </motion.div>

            </main>
        </div>
    );
};

export default HelpPage;